import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Select, MenuItem, Button, InputAdornment, Autocomplete, FormControl, FormHelperText } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { listAreas } from '../../services/areaService';

const PropertyFilter = ({
    onSearch,
    filters: externalFilters,
    onFilterChange,
    disabledFields = [],
    enableNavigation = true // New prop to enable/disable navigation
}) => {

    const [internalFilters, setInternalFilters] = useState({});
    const [openSelects, setOpenSelects] = useState({});
    const [areaOptions, setAreaOptions] = useState([]);
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        const fetchAreas = async () => {
            try {
                const data = await listAreas();
                if (data && Array.isArray(data)) {
                    setAreaOptions(data);
                }
            } catch (error) {
                console.error("Failed to fetch areas", error);
            }
        };
        fetchAreas();
    }, []);

    // Close open selects on scroll or wheel interactions (removed touchstart for iOS compatibility)
    useEffect(() => {
        const closeAll = () => setOpenSelects({});
        window.addEventListener('scroll', closeAll, { passive: true });
        window.addEventListener('wheel', closeAll, { passive: true });
        return () => {
            window.removeEventListener('scroll', closeAll);
            window.removeEventListener('wheel', closeAll);
        };
    }, []);

    // Use external filters if provided, otherwise use internal state
    const filters = externalFilters || internalFilters;

    const filterConfig = [
        {
            type: 'search',
            field: 'keyword',
            placeholder: 'Enter Keyword...',
            flex: 2,
            icon: SearchIcon
        },
        {
            type: 'select',
            field: 'type',
            placeholder: 'Type',
            flex: 1,
            options: [
                { value: 'residential', label: 'Residential' },
                { value: 'commercial', label: 'Commercial' },
                { value: 'agricultural', label: 'Agricultural' }
            ]
        },
        {
            type: 'select',
            field: 'area',
            placeholder: 'All Areas',
            flex: 1,
            options: areaOptions
        }
    ];

    // Add size field based on selected type
    if (filters.type === 'agricultural') {
        filterConfig.push({
            type: 'number',
            field: 'acres',
            placeholder: 'Acres',
            flex: 1
        });
    } else if (filters.type === 'commercial' || filters.type === 'residential') {
        filterConfig.push({
            type: 'number',
            field: 'sqYards',
            placeholder: 'Sq Yards',
            flex: 1
        });
    } else {
        // Default when no type is selected - show generic size field
        filterConfig.push({
            type: 'number',
            field: 'size',
            placeholder: 'Size',
            flex: 1
        });
    }

    const handleInputChange = (field, value) => {
        let newFilters = { ...filters, [field]: value };

        // Clear error for this field when user starts typing/selecting
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: false }));
        }

        // Clear size-related fields when type changes
        if (field === 'type') {
            // Clear all size fields when type changes
            delete newFilters.sqYards;
            delete newFilters.acres;
            delete newFilters.size;
        }

        if (onFilterChange) {
            onFilterChange(newFilters);
        } else {
            // If no external handler, update internal state
            setInternalFilters(newFilters);
        }
    };

    const handleSearch = () => {
        // Validate mandatory type field
        if (!filters.type) {
            setErrors(prev => ({ ...prev, type: true }));
            return;
        }

        // Clear error if type is present
        setErrors(prev => ({ ...prev, type: false }));

        // If navigation is enabled and we're on the home page, navigate based on type
        if (enableNavigation && filters.type) {
            const routeMap = {
                'residential': '/residential-plots-for-sale-in-amaravati',
                'commercial': '/commercial-plots-for-sale-in-amaravati',
                'agricultural': '/agriculture-lands-for-sale-in-amaravati'
            };

            const route = routeMap[filters.type];
            if (route) {
                // Navigate with filters as state so the target page can use them
                navigate(route, {
                    state: {
                        filters: filters,
                        fromSearch: true
                    }
                });
                return;
            }
        }

        // Default behavior - call onSearch if provided
        if (onSearch) {
            onSearch(filters);
        }
    };

    const handleClear = () => {
        const clearedFilters = {};
        filterConfig.forEach(config => {
            // Preserve values for disabled fields, clear others
            if (disabledFields.includes(config.field)) {
                clearedFilters[config.field] = filters[config.field] || '';
            } else {
                clearedFilters[config.field] = '';
            }
        });

        // Also clear any size fields that might not be in current config
        clearedFilters.sqYards = '';
        clearedFilters.acres = '';
        clearedFilters.size = '';

        setErrors({}); // Clear all errors on clear

        if (onFilterChange) {
            onFilterChange(clearedFilters);
        } else {
            setInternalFilters(clearedFilters);
        }
    };



    const renderField = (config) => {
        const isDisabled = disabledFields.includes(config.field);

        const commonStyles = {
            flex: { xs: '1 1 100%', md: config.flex },
            '& .MuiOutlinedInput-root': {
                backgroundColor: isDisabled ? '#f0f0f0' : '#fafafa',
                fontSize: '14px',
                borderRadius: '4px',
                height: '40px',
                '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderRadius: '4px'
                },
                '&:hover fieldset': {
                    borderColor: isDisabled ? '#e0e0e0' : '#bdbdbd'
                }
            }
        };

        if (config.type === 'search') {
            const Icon = config.icon;
            return (
                <TextField
                    key={config.field}
                    placeholder={config.placeholder}
                    value={filters[config.field] || ''}
                    onChange={(e) => handleInputChange(config.field, e.target.value)}
                    size="small"
                    disabled={isDisabled}
                    sx={{
                        ...commonStyles,
                        '& input::placeholder': {
                            color: '#999',
                            opacity: 1
                        }
                    }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Icon sx={{ color: '#bdbdbd', fontSize: '20px' }} />
                                </InputAdornment>
                            )
                        }
                    }}
                />
            );
        }

        if (config.type === 'number') {
            return (
                <TextField
                    key={config.field}
                    placeholder={config.placeholder}
                    value={filters[config.field] || ''}
                    onChange={(e) => handleInputChange(config.field, e.target.value)}
                    size="small"
                    type="number"
                    disabled={isDisabled}
                    sx={{
                        ...commonStyles,
                        '& input::placeholder': {
                            color: '#999',
                            opacity: 1
                        }
                    }}
                />
            );
        }

        if (config.type === 'select') {
            // Special handling for area field - make it searchable
            if (config.field === 'area') {
                return (
                    <Autocomplete
                        key={config.field}
                        fullWidth
                        size="small"
                        options={config.options}
                        getOptionLabel={(option) => option.label || ''}
                        value={config.options.find(option => option.value === filters[config.field]) || null}
                        onChange={(event, value) => handleInputChange(config.field, value ? value.value : '')}
                        disabled={isDisabled}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={config.placeholder}
                                sx={{
                                    ...commonStyles,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: isDisabled ? '#f0f0f0' : '#fafafa',
                                        fontSize: '14px',
                                        borderRadius: '4px',
                                        '& fieldset': {
                                            borderColor: '#e0e0e0',
                                            borderRadius: '4px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: isDisabled ? '#e0e0e0' : '#bdbdbd'
                                        }
                                    },
                                    '& input::placeholder': {
                                        color: '#999',
                                        opacity: 1
                                    }
                                }}
                            />
                        )}
                        sx={{
                            flex: { xs: '1 1 100%', md: config.flex }
                        }}
                    />
                );
            }

            // Regular select for other fields
            return (
                <FormControl
                    key={config.field}
                    error={!!errors[config.field]}
                    sx={{ ...commonStyles }}
                    size="small"
                >
                    <Select
                        value={filters[config.field] || ''}
                        onChange={(e) => handleInputChange(config.field, e.target.value)}
                        displayEmpty
                        size="small"
                        disabled={isDisabled}
                        fullWidth
                        sx={{
                            backgroundColor: isDisabled ? '#f0f0f0' : '#fafafa',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: errors[config.field] ? '#d32f2f' : '#e0e0e0'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: errors[config.field] ? '#d32f2f' : (isDisabled ? '#e0e0e0' : '#bdbdbd')
                            },
                            '& .MuiSelect-select': {
                                color: filters[config.field] ? '#333' : '#999'
                            }
                        }}
                    >
                        <MenuItem value="" sx={{ fontSize: '14px', color: '#999' }}>
                            {config.placeholder}
                        </MenuItem>
                        {config.options.map(option => (
                            <MenuItem key={option.value} value={option.value} sx={{ fontSize: '14px' }}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors[config.field] && (
                        <FormHelperText sx={{
                            color: '#d32f2f',
                            fontSize: '11px',
                            m: 0,
                            mt: 0.5,
                            ml: 0.5
                        }}>
                            {config.field === 'type' ? 'Type is mandatory' : 'Required'}
                        </FormHelperText>
                    )}
                </FormControl>
            );
        }

        return null;
    };

    return (
        <div
            style={{
                paddingTop: '20px',
                paddingBottom: '20px',
                backgroundColor: '#f5f5f5'
            }}
        >
            <div className="container">
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 1,
                        alignItems: { xs: 'stretch', md: 'flex-start' },
                        backgroundColor: '#fff',
                        p: 1.5,
                        borderRadius: '4px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        border: '1px solid #e0e0e0'
                    }}
                >
                    {/* Dynamically render main filters */}
                    {filterConfig.map(config => renderField(config))}

                    {/* Clear Button */}
                    <Button
                        variant="outlined"
                        onClick={handleClear}
                        size="small"
                        sx={{
                            minWidth: '70px',
                            color: '#666',
                            borderColor: '#e0e0e0',
                            fontSize: '13px',
                            fontWeight: '500',
                            textTransform: 'none',
                            borderRadius: '4px',
                            height: '40px',
                            '&:hover': {
                                borderColor: '#bdbdbd',
                                backgroundColor: '#fafafa'
                            }
                        }}
                    >
                        Clear
                    </Button>

                    {/* Go Button */}
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        size="small"
                        sx={{
                            minWidth: '70px',
                            backgroundColor: '#D8B025',
                            color: '#FFFFFF',
                            fontSize: '13px',
                            fontWeight: '600',
                            textTransform: 'none',
                            borderRadius: '4px',
                            height: '40px',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#c5a02f',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Go
                    </Button>
                </Box>


            </div>
        </div>
    );
};

export default PropertyFilter;
